export {Playlist as Page};

import {Helmet} from "react-helmet-async";
import {forwardRef, lazy, Suspense} from "preact/compat";
import {useEffect, useState, useCallback, useRef} from "preact/hooks";
import {Song} from "../../components/Song.jsx";
import messages from "../../messages/messages.js";
import API_URL, {withSite} from "../../includes/api.js";
import {usePageContext} from "../../hooks/usePageContext.js";
import {RingSpinner} from "../../components/Spinner.jsx";

const VirtuosoGrid = lazy(() =>
    import("react-virtuoso").then((module) => ({default: module.VirtuosoGrid}))
);

function Playlist() {

    const pageContext = usePageContext();
    const p = pageContext?.urlParsed?.search?.p ?? '';
    // Seeded from +data.shared.js's server-side fetch (same endpoint/page size as
    // fetchSongs()) so Helmet and the first songs are correct before hydration.
    const initialData = pageContext?.data;
    const hasSeededData = Boolean(initialData?.playlist);
    const isFirstRun = useRef(true);
    // VirtuosoGrid throws under preact-render-to-string, so SSR/hydration
    // render a plain grid; this flips to virtualized right after mount (client-only).
    const [virtualized, setVirtualized] = useState(false);
    useEffect(() => {
        setVirtualized(true);
    }, []);

    const PAGE_SIZE = 50;
    const isFetchingRef = useRef(false);
    const requestIdRef = useRef(0);

    const [playlist, setPlaylist] = useState(initialData?.playlist ?? []);
    const [songs, setSongs] = useState(initialData?.songs ?? []);
    const [loading, setLoading] = useState(!hasSeededData);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(() => {
        if (!hasSeededData) return false;
        const seededSongs = initialData?.songs ?? [];
        return initialData?.apiHasMore ?? (initialData?.total !== null
            ? seededSongs.length < initialData.total
            : seededSongs.length === PAGE_SIZE);
    });
    const [nextOffset, setNextOffset] = useState(() => (hasSeededData ? (initialData?.songs ?? []).length : 0));

    const fetchSongs = useCallback((offset = 0, reset = false) => {
        if (isFetchingRef.current && !reset) {
            return;
        }

        isFetchingRef.current = true;
        const requestId = ++requestIdRef.current;

        if (offset === 0) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }

        fetch(withSite(API_URL + "/playlist/get?p=" + encodeURIComponent(p) + "&limit=" + PAGE_SIZE + "&offset=" + offset))
            .then((response) => response.json())
            .then((data) => {
                if (requestId !== requestIdRef.current || data.code !== 200) {
                    return;
                }

                const playlistData = data.data?.playlist ?? {};
                const fetchedSongs = data.data?.songs ?? {};

                const total = Number(data.data?.pagination?.total ?? data.data?.total);
                const totalCount = Number.isFinite(total) ? total : null;
                const apiHasMore = data.data?.has_more ?? null;

                setPlaylist(playlistData);
                setSongs((currentSongs) => {
                    const baseSongs = reset ? [] : currentSongs;
                    const nextSongs = [...baseSongs, ...fetchedSongs];
                    const inferredHasMore = apiHasMore ?? (totalCount !== null
                        ? nextSongs.length < totalCount
                        : fetchedSongs.length === PAGE_SIZE);

                    setNextOffset(nextSongs.length);
                    setHasMore(inferredHasMore);

                    return nextSongs;
                });
            })
            .finally(() => {
                if (requestId !== requestIdRef.current) {
                    return;
                }
                isFetchingRef.current = false;
                setLoading(false);
                setLoadingMore(false);
            });
    }, [p]);

    const loadMoreSongs = useCallback(() => {
        if (loading || loadingMore || !hasMore) {
            return;
        }

        fetchSongs(nextOffset);
    }, [fetchSongs, hasMore, loading, loadingMore, nextOffset]);

    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            if (hasSeededData) {
                // Server already fetched this exact playlist — don't
                // reset-then-refetch on mount, just keep what's seeded.
                return;
            }
        }

        setPlaylist({});
        setSongs([]);
        setNextOffset(0);
        setHasMore(false);

        fetchSongs(0, true);
    }, [fetchSongs]);

    return (
        <section className="text-white">

            <Helmet>
                <title>{messages["SITE.NAME"]} | {playlist?.category_name || ''} Playlist</title>
                <meta name="description"
                      content={`Listen to ${messages["SITE.NAME"] || ''} ${playlist?.category_name || ''} Playlist songs`}/>
            </Helmet>

            <div>
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                        <h1>{playlist.category_name} playlist</h1>
                        <p className="mt-2">{playlist.description}</p>
                    </div>

                    <div className="relative rounded-lg border border-border/60 bg-center bg-cover overflow-hidden" style={{backgroundImage: `url('${playlist.image_url}')`}}>
                        <div className="absolute inset-0 w-full h-full backdrop-blur-md"></div>
                        <div className="relative w-full h-60 bg-contain bg-center bg-no-repeat" style={{backgroundImage: `url('${playlist.image_url}')`}}></div>
                    </div>
                </div>

                {loading ? (
                    <div className="mt-12">
                        <div role="status">
                            <RingSpinner className="inline w-8 h-8 mr-2 text-gray-200 animate-spin text-gray-600 fill-red-400" trackFill="currentColor"/>
                        </div>
                        <h2 className="text-2xl font-bold">Loading...</h2>
                    </div>
                ) : (
                    songs.length === 0 ? (
                        <div className="mt-12">
                            <h2 className="text-2xl font-bold">No songs found</h2>
                            <p className="mt-2">This playlist is empty</p>
                            <a href="/"
                               className="inline-block py-2 px-5 text-base font-medium text-center text-white rounded-lg border border-gray-300 hover:bg-white hover:text-black cursor-pointer">Go
                                back</a>
                        </div>
                    ) : (
                        virtualized ? (
                            <Suspense fallback={<div className="mt-12 text-center text-sm text-gray-300">Loading playlist...</div>}>
                                <div className="mt-12">
                                    <VirtuosoGrid
                                        useWindowScroll={true}
                                        data={songs}
                                        endReached={loadMoreSongs}
                                        overscan={600}
                                        computeItemKey={(_, song) => song.youtube_url}
                                        components={{
                                            List: forwardRef(({children, className, ...props}, ref) => (
                                                <div
                                                    ref={ref}
                                                    {...props}
                                                    className={`${className || ""} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6`}
                                                >
                                                    {children}
                                                </div>
                                            ))
                                        }}
                                        itemContent={(_, song) => <Song song={song} hideHr={true}/>}
                                    />
                                </div>
                            </Suspense>
                        ) : (
                            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {songs.map((song) => <Song key={song.youtube_url} song={song} hideHr={true}/>)}
                            </div>
                        )
                    )
                )}

            </div>

        </section>
    );
}
